import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save, Globe } from "lucide-react";
import { HandbookEditorPanel } from "@/components/handbook/handbook-editor-panel";
import {
  handbookFormSchema,
  type HandbookFormValues,
} from "@/lib/validation/handbook-schema";
import { useCreateHandbook } from "@/hooks/use-handbook";

export default function HandbookNewPage() {
  // 각주 영역이 본문 아래 추가되면서 page 길이가 viewport 를 넘는 케이스가 잦아짐.
  // useHtmlOverflowHidden 으로 root 스크롤을 잠그면 panel 내부 overflow-auto 만 의존하게
  // 되는데 sticky 헤더 + flex 레이아웃 조합에서 안정적으로 스크롤이 안 되는 경우가 있어
  // 페이지 단의 일반 스크롤로 갈음.
  const navigate = useNavigate();
  const createMutation = useCreateHandbook();

  const methods = useForm<HandbookFormValues>({
    resolver: zodResolver(handbookFormSchema),
    defaultValues: {
      slug: "",
      title: "",
      content: "",
      excerpt: "",
      tags: [],
      subtitle: "",
      thumbnailUrl: "",
      parentIds: [],
      relatedIds: [],
      aboutSlot: null,
      footnotes: [],
    },
  });

  const submit = (publishNow: boolean) =>
    methods.handleSubmit(async (values) => {
      // 슬롯 doc 은 위계/관계 없음 — 안전망으로 parentIds/relatedIds 전송 생략.
      // UI 도 disable 되지만 stale form state 가 남아있을 수 있어 여기서 한 번 더.
      const isSlotDoc = values.aboutSlot != null;
      const result = await createMutation.mutateAsync({
        slug: values.slug,
        title: values.title,
        content: values.content,
        excerpt: values.excerpt || undefined,
        tags: values.tags,
        publishNow,
        subtitle: values.subtitle || undefined,
        thumbnailUrl: values.thumbnailUrl || undefined,
        parentIds:
          !isSlotDoc && values.parentIds.length > 0
            ? values.parentIds
            : undefined,
        relatedIds:
          !isSlotDoc && values.relatedIds.length > 0
            ? values.relatedIds
            : undefined,
        aboutSlot: values.aboutSlot ?? undefined,
        footnotes: values.footnotes.length > 0 ? values.footnotes : undefined,
      });
      if (result.success && result.handbook) {
        navigate(`/handbook/${result.handbook._id}/edit`, { replace: true });
      }
    });

  const isPending = createMutation.isPending;

  return (
    <FormProvider {...methods}>
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-center justify-between border-b px-4 py-2 md:px-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/handbook")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            목록
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={submit(false)}
              disabled={isPending}
            >
              <Save className="mr-1 h-4 w-4" />
              임시저장
            </Button>
            <Button size="sm" onClick={submit(true)} disabled={isPending}>
              <Globe className="mr-1 h-4 w-4" />
              저장 후 발행
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <HandbookEditorPanel />
        </div>
      </div>
    </FormProvider>
  );
}
